import type { CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import { EmailChip } from "./EmailChip";
import { tokens } from "./tokens";

/**
 * The email field that stays a field. Empty, it is an input; once an address
 * is settled it holds that address as a chip and nothing else — the field does
 * not turn into something else, the chip sits inside it. Removing the chip
 * gives the input back.
 */
export function EmailField({
  chip,
  input,
  trailing,
  style,
}: {
  /** The settled address, or null while the visitor is typing. */
  chip: {
    email: string;
    lissen?: boolean;
    onRemove?: () => void;
    removeLabel?: string;
    removing?: boolean;
  } | null;
  /** The host's own input, spread onto the bare control inside the field. */
  input?: InputHTMLAttributes<HTMLInputElement>;
  /** Something quiet on the right: a status, a spinner. */
  trailing?: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <div
      style={{
        alignItems: "center",
        background: tokens.surface,
        border: `${tokens.borderWidth} solid ${tokens.border}`,
        borderRadius: tokens.radius,
        boxSizing: "border-box",
        color: tokens.text,
        display: "flex",
        gap: tokens.space,
        minHeight: tokens.controlHeight,
        padding: chip
          ? `0 calc(${tokens.space} * 0.75)`
          : `0 calc(${tokens.space} * 1.5)`,
        width: "100%",
        ...style,
      }}
      onClick={(event) => {
        event.currentTarget.querySelector("input")?.focus();
      }}
    >
      {chip ? (
        <EmailChip
          email={chip.email}
          lissen={chip.lissen}
          onRemove={chip.onRemove}
          removeLabel={chip.removeLabel}
          disabled={chip.removing}
        />
      ) : (
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          {...input}
          style={{
            background: "transparent",
            border: 0,
            color: "inherit",
            flex: 1,
            font: "inherit",
            minWidth: 0,
            outline: "none",
            padding: 0,
            ...input?.style,
          }}
        />
      )}
      {trailing}
    </div>
  );
}
